import { useState, useCallback, useRef } from 'react';

/** Reads a sessionStorage-persisted JSON value; `fallback` on any miss or parse error. */
export function readStorage(key, fallback) {
  try {
    const raw = sessionStorage.getItem(key);
    return raw != null ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

export function writeStorage(key, value) {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // quota exceeded or private browsing — silently ignore
  }
}

/**
 * Drop-in `useState` replacement backed by `sessionStorage`.
 *
 * Pass `null` or `undefined` as the key to disable persistence
 * (behaves as plain `useState`).
 *
 * @param {string|null} key  sessionStorage key
 * @param {*} defaultValue   initial value when nothing is stored
 */
export default function usePersistedState(key, defaultValue) {
  const keyRef = useRef(key);
  keyRef.current = key;

  const [value, setValueRaw] = useState(() => (key ? readStorage(key, defaultValue) : defaultValue));

  const setValue = useCallback(updater => {
    setValueRaw(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      if (keyRef.current) writeStorage(keyRef.current, next);
      return next;
    });
  }, []);

  return [value, setValue];
}
